import { GATE } from '@/config/audio';
import { DEFAULTS, RHYTHM } from '@/config/training';
import type { AudioEngine } from '@/audio/engine/AudioEngine';
import { Metronome, type BeatInfo } from '@/audio/metronome/Metronome';
import type { BeatHit, SessionPhase, SessionSummary, TimingVerdict } from '@/types/training';
import { Observable } from '../observable';
import { summarize } from '../summary';
import { TimingMatcher } from '../timingMatcher';

export interface RhythmSettings {
  bpm: number;
  durationMin: number;
  clickEnabled: boolean;
}

export interface RhythmSnapshot {
  phase: SessionPhase;
  countInBeat: number;
  beatInBar: number;
  bpm: number;
  elapsedSec: number;
  totalSec: number;
  /** Verdict of the most recently resolved beat. */
  lastVerdict: TimingVerdict | null;
  score: number;
  hits: number;
  summary: SessionSummary | null;
}

/**
 * Rhythm trainer: one downstroke per beat.
 *
 * All it measures is *when* the string was struck relative to the click. It
 * makes no claim about which strings were hit or in which direction — see
 * docs/research.md §3.
 */
export class RhythmEngine {
  readonly store: Observable<RhythmSnapshot>;

  private metronome: Metronome | null = null;
  private readonly matcher = new TimingMatcher(RHYTHM.hitWindowMaxMs / 1000);
  private unsubscribeOnset: (() => void) | null = null;
  private unsubscribeBeat: (() => void) | null = null;
  private countInRemaining = DEFAULTS.countInBeats;
  private practisedSec = 0;
  private lastBeatTime = 0;
  private startedAt = Date.now();
  private rafId: number | null = null;
  private finishing = false;

  constructor(
    private readonly audio: AudioEngine,
    private readonly settings: RhythmSettings,
    private readonly onFinished?: (summary: SessionSummary) => void,
  ) {
    this.store = new Observable<RhythmSnapshot>({
      phase: 'idle',
      countInBeat: 0,
      beatInBar: -1,
      bpm: settings.bpm,
      elapsedSec: 0,
      totalSec: settings.durationMin * 60,
      lastVerdict: null,
      score: 0,
      hits: 0,
      summary: null,
    });
  }

  get snapshot(): RhythmSnapshot {
    return this.store.getSnapshot();
  }

  start(): void {
    const context = this.audio.audioContext;
    if (!context) throw new Error('AudioEngine has no context');

    this.startedAt = Date.now();
    this.metronome = new Metronome(context);
    this.metronome.configure({ bpm: this.settings.bpm, beatsPerBar: 4, audible: this.settings.clickEnabled });
    this.matcher.setBeatInterval(60 / this.settings.bpm);
    // A gated onset can arrive up to eventDecisionSec after it was struck;
    // keep its beat pending until then (see TimingMatcher.setResolveLag).
    this.matcher.setResolveLag(GATE.eventDecisionSec);

    this.audio.setStringAnalysis(false);
    this.audio.setTarget(null);
    this.unsubscribeOnset = this.audio.onOnset((onset) => this.matcher.addOnset(onset.time));
    this.unsubscribeBeat = this.metronome.onBeat(this.handleBeat);

    this.countInRemaining = DEFAULTS.countInBeats;
    this.store.update({ phase: 'countIn', countInBeat: 1 });
    this.metronome.start();
    this.pump();
  }

  pause(): void {
    if (this.snapshot.phase !== 'running' && this.snapshot.phase !== 'countIn') return;
    this.metronome?.stop();
    // Beats already scheduled will never arrive now; do not score them.
    this.matcher.dropPending();
    this.store.update({ phase: 'paused', beatInBar: -1 });
  }

  resume(): void {
    if (this.snapshot.phase !== 'paused' || !this.metronome) return;
    this.countInRemaining = DEFAULTS.countInBeats;
    // The audio clock kept running while paused. Without this, the first beat
    // after the count-in would add the whole pause to the practised time — a
    // two-minute break would count as two minutes of playing and could end the
    // session the moment it resumed.
    this.lastBeatTime = 0;
    this.store.update({ phase: 'countIn', countInBeat: 1 });
    this.metronome.start();
  }

  finish(): void {
    if (this.finishing) return;
    this.finishing = true;
    this.metronome?.stop();
    this.dispose();

    const summary = summarize({
      mode: 'rhythm',
      startedAt: this.startedAt,
      durationSec: Math.round(this.practisedSec),
      chordIds: [],
      bpmStart: this.settings.bpm,
      bpmEnd: this.settings.bpm,
      attempts: [],
      transitions: [],
      rhythmScore: this.matcher.score(),
      clarityScore: null,
    });
    this.store.update({ phase: 'finished', summary, score: this.matcher.score() });
    this.onFinished?.(summary);
  }

  /** Resolved beats, recomputed against the final latency estimate. */
  get hits(): BeatHit[] {
    return this.matcher.finalHits();
  }

  dispose(): void {
    this.unsubscribeOnset?.();
    this.unsubscribeBeat?.();
    this.unsubscribeOnset = null;
    this.unsubscribeBeat = null;
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.rafId = null;
  }

  private handleBeat = (beat: BeatInfo): void => {
    if (this.finishing) return;

    if (this.countInRemaining > 0) {
      // Counted up: a count-in is "one, two, three, four", not a countdown.
      const beatNumber = DEFAULTS.countInBeats - this.countInRemaining + 1;
      this.store.update({ phase: 'countIn', countInBeat: beatNumber, beatInBar: -1 });
      this.countInRemaining--;
      return;
    }

    if (this.lastBeatTime > 0) this.practisedSec += beat.time - this.lastBeatTime;
    this.lastBeatTime = beat.time;

    this.matcher.expect(beat.index, beat.time);
    this.store.update({
      phase: 'running',
      countInBeat: 0,
      beatInBar: beat.beatInBar,
      elapsedSec: this.practisedSec,
    });

    if (this.practisedSec >= this.settings.durationMin * 60) this.finish();
  };

  /**
   * Beats are resolved on their own animation-frame loop rather than on the
   * next beat: a hit that arrives late still belongs to its own beat, and its
   * window closes some milliseconds after the beat has passed.
   */
  private pump = (): void => {
    const context = this.audio.audioContext;
    if (!context || this.finishing) return;
    if (this.snapshot.phase === 'paused') {
      this.rafId = requestAnimationFrame(this.pump);
      return;
    }
    const resolved = this.matcher.collect(context.currentTime);
    if (resolved.length > 0) {
      const last = resolved[resolved.length - 1];
      this.store.update({
        lastVerdict: last.verdict,
        score: this.matcher.score(),
        hits: this.snapshot.hits + resolved.filter((h) => h.onsetTime !== null).length,
      });
    }
    this.rafId = requestAnimationFrame(this.pump);
  };
}
